import { useEffect, useState } from "react";
import { Button, Form, Col, Row, Modal } from "antd";
import useGet from "../../../global/hooks/useGet";
import usePost from "../../../global/hooks/usePost";
import usePut from "../../../global/hooks/usePut";
import CustomInput from "../../../global/components/inputWidgets/CustomInput";
import { useNavigate, useParams } from "react-router-dom";
import {
  openConfirmNotification,
  openErrorNotification,
} from "../../../global/utils/toast";
import CommonBreadcrumb from "../../../global/components/CommonBreadcrumb";
import CommonButton from "../../../global/components/CommonButton";
import CustomSelect from "../../../global/components/inputWidgets/CustomSelect";
import { getModifiedResponse } from "../../../global/utils/responseModifier";
import {
  CheckCircleOutlined,
  CloseOutlined,
  UnorderedListOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import useDelete from "../../../global/hooks/useDelete";
import ConfirmModal from "../../../global/components/ConfirmModal";
import AccountCreationModal from "../../../global/components/AccountCreationModal";
import { FaFileInvoiceDollar } from "react-icons/fa6";
import DealerIcon from "../../../assets/images/vectors/SideBarIcons/DealerIcon";
import OemIcon from "../../../assets/images/vectors/SideBarIcons/OemIcon";
import PaymentIcon from "../../../assets/images/vectors/SideBarIcons/PaymentIcon";
import dayjs from "dayjs";
import CustomDateTimePicker from "../../../global/components/inputWidgets/CustomDatePicker";
import { FaDollarSign } from "react-icons/fa";
import { GlobalTable } from "../../../global/components/GlobalTable";
import { PurchaseItemsColumns } from "./PurchaseItemsColumns";
import PurchaseItemModal from "./PurchaseItemModal";
import MyPaymentForm from "../../../global/components/PaymentForm";
import {
  getPurchaseListPath,
  getPurchaseRowId,
} from "../utils/listSearchStorage";
import {
  getDealerNo,
  getOemNo,
  hasRole,
} from "../../../global/hooks/getUserRole";

export const PurchaseForm = ({ type, onCancel }) => {
  const userData = JSON.parse(sessionStorage.getItem("userData"));
  /** Dealer login: no OEM/Dealer pickers; scope from session (see Device filter customer path). */
  const hideOemDealerForDealerAccount = hasRole(7, 8);
  const role_value = userData?.role_value;
  const isSystemUser = hasRole(1, 2, 3);
  const { id } = useParams();
  const [showModal, setShowModal] = useState(false);
  const [paymentModal, setPaymentModal] = useState(false);
  const [showCardLayout, setShowCardLayout] = useState(!isSystemUser);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [modalType, setModalType] = useState("");
  const [defaultData, setDefaultData] = useState({
    OemNo: null,
    DealerNo: null,
  });
  const [oemNo, setOemNo] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [showItemsModal, setShowItemsModal] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [totalAmount, setTotalAmount] = useState(null);
  const purchaseItemurl =
    id && `${process.env.REACT_APP_API_URL}/api/purchases/${id}/items/`;
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const url = id && `${process.env.REACT_APP_API_URL}/api/purchases/${id}/`;
  const postUrl = `${process.env.REACT_APP_API_URL}/api/purchases/`;
  const putUrl = `${process.env.REACT_APP_API_URL}/api/purchases/${id}/`;
  const deleteUrl = `${process.env.REACT_APP_API_URL}/api/purchases/${id}/`;
  const deleteItemsUrl = `${process.env.REACT_APP_API_URL}/api/purchases/${id}/items/bulk-delete/`;
  const optionUrl = `${process.env.REACT_APP_API_URL}/api/accounts/options/`;
  const { data, loading, error, refetch } = useGet(url);

  useEffect(() => {
    if (!paymentModal) setShowCardLayout(!isSystemUser);
  }, [paymentModal, isSystemUser]);

  const hasApiDiscounted =
    data?.data?.Amount_with_discount != null &&
    data?.data?.Amount_with_discount !== "";
  const discountedFromApi = hasApiDiscounted
    ? Number(data.data.Amount_with_discount)
    : NaN;
  const baseFromApi = Number(data?.data?.Amount);
  const purchaseAmountNum = Number.isFinite(discountedFromApi)
    ? discountedFromApi
    : Number.isFinite(baseFromApi)
      ? baseFromApi
      : Number(totalAmount);
  const purchaseAmountProp =
    Number.isFinite(purchaseAmountNum) && purchaseAmountNum >= 0
      ? `$${purchaseAmountNum.toFixed(2)}`
      : "$0.00";
  const isPaid = data?.data?.PaymentNo != null;

  const {
    data: purchaseData,
    loading: purchaseLoading,
    error: purchaseError,
    refetch: purchaseRefetch,
  } = useGet(purchaseItemurl);

  const {
    data: optionData,
    loading: optionDataLoading,
    error: optionDataError,
    refetch: optionDataRefetch,
  } = useGet(optionUrl);
  const resolveOemNo = (value) => {
    if (value == null || value === "") return null;
    const numeric = Number(value);
    if (Number.isFinite(numeric)) return numeric;
    const oemOption = optionData?.data?.oems?.find(
      (oem) => String(oem?.name) === String(value),
    );
    return oemOption?.value ?? null;
  };
  const resolvedOemNo =
    resolveOemNo(oemNo) ??
    resolveOemNo(defaultData?.OemNo) ??
    resolveOemNo(data?.data?.OemNo) ??
    (hideOemDealerForDealerAccount ? resolveOemNo(getOemNo()) : null);
  const dealerOptionsUrl =
    resolvedOemNo != null
      ? `${process.env.REACT_APP_API_URL}/api/dealers/oems/${resolvedOemNo}/dealers/`
      : null;
  const {
    data: dealerOptionsData,
    loading: dealerOptionsLoading,
    refetch: dealerOptionsRefetch,
  } = useGet(dealerOptionsUrl);
  const modifiedOems = getModifiedResponse(optionData, "oems");
  const modifiedDealers = getModifiedResponse(dealerOptionsData, null);

  const resolveDealerBillingType = (dealerNoValue) => {
    if (dealerNoValue === null || dealerNoValue === undefined) return null;
    if (dealerNoValue === "") return null;

    const dealerId = Number(dealerNoValue);
    if (!Number.isFinite(dealerId)) return null;

    const rawDealers = dealerOptionsData?.data;
    if (!Array.isArray(rawDealers)) return null;

    const dealer = rawDealers.find(
      (d) =>
        Number(d?.value) === dealerId ||
        Number(d?.DealerNo) === dealerId ||
        Number(d?.dealer_no) === dealerId,
    );

    const bt =
      dealer?.BillingType ??
      dealer?.billing_type ??
      dealer?.billingType ??
      dealer?.Billing_type ??
      null;

    if (bt === null || bt === undefined) return null;
    const btNum = Number(bt);
    return Number.isFinite(btNum) ? btNum : null;
  };

  const effectiveDealerNoForPayment =
    data?.data?.DealerNo ??
    form.getFieldValue("DealerNo") ??
    defaultData?.DealerNo ??
    (hideOemDealerForDealerAccount ? getDealerNo() : null);

  const dealerBillingTypeForPayment = resolveDealerBillingType(
    effectiveDealerNoForPayment,
  );

  const { error: postError, postData: postAction } = usePost(postUrl);
  const { error: putError, putData: putAction } = usePut(putUrl);
  const {
    data: deletedData,
    loading: deletedDataLoading,
    error: deletedDataError,
    deleteData,
  } = useDelete(deleteUrl);
  const {
    data: deletedItemsData,
    loading: deletedItemsDataLoading,
    error: deletedItemsDataError,
    deleteMultiple: deleteItemsData,
  } = useDelete(deleteItemsUrl);

  const onFinish = async (values) => {
    const { OemNo: formOemNo, ...rest } = values;
    const modValues = {
      ...rest,
      ...(hideOemDealerForDealerAccount
        ? {
          OemNo: getOemNo(),
          DealerNo: getDealerNo(),
        }
        : {
          OemNo: formOemNo,
        }),
    };
    try {
      if (id) {
        const result = await putAction(modValues);
        if (result != null) {
          openConfirmNotification("Success");
          setTimeout(() => {
            navigate(`/purchase/info/${id}`);
          }, 200);
        }
      } else {
        const result = await postAction(modValues);
        if (result != null) {
          openConfirmNotification("Success");
          setTimeout(() => {
            const rid = getPurchaseRowId(result?.data ?? result);
            navigate(
              rid != null ? `/purchase/info/${rid}` : getPurchaseListPath(),
            );
          }, 200);
        }
      }
    } catch (error) {
      openErrorNotification("Something went wrong.");
      console.log(error);
    }
  };

  const onReset = () => {
    onCancel ? onCancel() : navigate(-1);
  };

  const getPurchasePaidPayload = (paymentResponse) => {
    const values = form.getFieldsValue(true);
    const amount = Number(
      String(values?.Amount ?? "").replace(/[^0-9.-]+/g, ""),
    );
    const paymentNo =
      paymentResponse?.data?.PaymentNo ?? paymentResponse?.PaymentNo ?? null;

    return {
      PurchaseName: values?.PurchaseName ? String(values.PurchaseName) : "",
      PurchaseDesc: values?.PurchaseDesc ? String(values.PurchaseDesc) : "",
      OemNo: hideOemDealerForDealerAccount ? getOemNo() : values?.OemNo,
      DealerNo: hideOemDealerForDealerAccount
        ? getDealerNo()
        : values?.DealerNo,
      Amount: Number.isFinite(amount) ? amount : 0,
      PaymentNo: paymentNo,
    };
  };

  const onDeleteConfirm = async () => {
    try {
      await deleteData();
    } catch (error) {
      openErrorNotification("Something went wrong.");
    }
    setShowModal(false);
  };
  const onDeleteItemsConfirm = async () => {
    try {
      await deleteItemsData({ ids: selectedRowKeys });
    } catch (error) {
      openErrorNotification("Something went wrong.");
    }
    setShowItemsModal(false);
    setSelectedRowKeys([]);
  };

  useEffect(() => {
    if (!id || loading) return;
    const getAccountInfoById = () => {
      try {
        form.resetFields();
        if (data?.data) {
          form.setFieldsValue({
            PurchaseName: String(data.data.PurchaseName),
            PurchaseDesc: data.data.PurchaseDesc
              ? String(data.data.PurchaseDesc)
              : "",
            OemNo: data?.data?.OemNo,
            OemName: data?.data?.OemNo ? String(data.data.OemNo) : "",
            DealerNo: data?.data?.DealerNo,
            Amount: data.data.Amount
              ? `$${Number(data.data.Amount).toFixed(2)}`
              : "",
            Amount_with_discount: data.data.Amount_with_discount
              ? `$${Number(data.data.Amount_with_discount).toFixed(2)}`
              : "",
            PaymentTime: data.data.PaymentTime
              ? dayjs(data.data.PaymentTime)
              : null,
          });
        } else {
          console.warn("No data found for the given ID");
        }
      } catch (error) {
        if (!loading) {
          openErrorNotification("Error fetching data!");
        }
      }
    };

    getAccountInfoById();
  }, [id, data, loading, form, type]);

  useEffect(() => {
    const amountFromInfo = Number(data?.data?.Amount);
    if (Number.isFinite(amountFromInfo)) {
      setTotalAmount(amountFromInfo);
      form.setFieldsValue({
        Amount: `$${amountFromInfo.toFixed(2)}`,
      });
    }
  }, [data, form]);

  useEffect(() => {
    if (postError) {
      openErrorNotification(
        postError?.response?.data?.message || "Something went wrong",
      );
    }
  }, [postError]);

  useEffect(() => {
    if (putError) {
      openErrorNotification(
        putError?.response?.data?.message || "Something went wrong",
      );
    }
  }, [putError]);

  //toast for delete
  useEffect(() => {
    if (deletedData && !deletedDataError) {
      openConfirmNotification("Success");
      navigate(getPurchaseListPath());
    } else if (deletedDataError) {
      openErrorNotification(
        deletedDataError?.response?.data?.message || "Something went wrong",
      );
    } else {
      return;
    }
  }, [deletedData, deletedDataError]);

  useEffect(() => {
    if (deletedItemsData && !deletedItemsDataError) {
      openConfirmNotification("Success");
      purchaseRefetch();
      refetch();
    } else if (deletedItemsDataError) {
      openErrorNotification(
        deletedItemsDataError?.response?.data?.message ||
        "Something went wrong",
      );
    } else {
      return;
    }
  }, [deletedItemsData, deletedItemsDataError]);

  const breadcrumbItems = [
    {
      label: "Purchase",
      href: "/purchase",
    },
    {
      label: id ? (type === "info" ? "Information" : "Edit") : "Add",
      href: id
        ? type === "info"
          ? `/purchase/info/${id}`
          : `/purchase/edit/${id}`
        : "/purchase/add",
    },
  ];
  const infoActionsReady =
    type === "info" &&
    Boolean(id) &&
    !loading &&
    Boolean(data?.data) &&
    (dealerOptionsUrl == null || !dealerOptionsLoading);
  /** System roles (1, 2, 7, 8) always see Payment when purchase is payable; others follow dealer billing type. */
  const showPaymentOnInfo =
    infoActionsReady &&
    !isPaid &&
    Number.isFinite(purchaseAmountNum) &&
    purchaseAmountNum > 0 &&
    (hasRole(1, 2, 7, 8) ||
      (dealerBillingTypeForPayment == null
        ? true
        : dealerBillingTypeForPayment === 1));

  const buttonsData =
    type === "info"
      ? [
        ...(infoActionsReady && !isPaid
          ? [
            ...(showPaymentOnInfo
              ? [
                {
                  label: "Payment",
                  icon: <FaDollarSign />,
                  onClick: () => setPaymentModal(true),
                },
              ]
              : []),
            {
              label: "Edit",
              icon: <EditOutlined />,
              onClick: () => {
                navigate(`/purchase/edit/${id}`);
              },
            },
            {
              label: "Delete",
              icon: <DeleteOutlined />,
              onClick: () => {
                setShowModal(true);
              },
            },
          ]
          : []),
        {
          label: "List",
          icon: <UnorderedListOutlined />,
          onClick: () => {
            navigate(getPurchaseListPath());
          },
        },
      ].filter(Boolean)
      : [
        {
          label: "Apply",
          icon: <CheckCircleOutlined />,
          onClick: () => form.submit(),
          disabled: isPaid,
        },
        {
          label: "Cancel",
          icon: <CloseOutlined />,
          onClick: onReset,
        },
      ];

  const handleCreateNew = (type) => {
    setModalType(type);
    setIsOpenModal(true);
  };
  useEffect(() => {
    optionDataRefetch();
    dealerOptionsRefetch();
    if (defaultData?.DealerNo) {
      form.setFieldsValue(defaultData);
    }
  }, [defaultData]);

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    fixed: true,
  };

  const purchaseItemButtonsData = [
    {
      label: "Add",
      icon: <PlusOutlined />,
      onClick: () => {
        // navigate(`/productionitem/add/${id}`);
        setOpenModal(true);
      },
    },
    {
      label: "Delete",
      icon: <DeleteOutlined />,
      onClick: () => {
        setShowItemsModal(true);
      },
      disabled: selectedRowKeys?.length === 0 ? true : false,
    },
  ];

  return (
    <>
      <AccountCreationModal
        isOpenModal={isOpenModal}
        modalType={modalType}
        setIsOpenModal={setIsOpenModal}
        setDefaultData={setDefaultData}
        defaultData={defaultData}
        setModalType={setModalType}
        isModal={true}
        isModalForm={true}
        title={
          <div className="flex items-center bg-gray-200 p-2 rounded-md">
            {modalType === "Dealer" ? (
              <DealerIcon className="text-[15px] text-black ml-1" />
            ) : modalType === "OEM" ? (
              <OemIcon className="text-[15px] text-black ml-1" />
            ) : null}
            <span className="text-[14px] font-[Poppins] text-black  ml-2">
              {isOpenModal ? `Add ${modalType}` : "Send Invitation"}
            </span>
          </div>
        }
      />
      <Form
        validateTrigger={"onChange"}
        form={form}
        onFinish={onFinish}
        scrollToFirstError
        layout={"vertical"}
        autoComplete={"off"}
      >
        <div>
          <div className="pb-4 sticky -mt-4 pt-[6px] top-[55px] z-10 bg-[#f4f5f9]">
            <CommonBreadcrumb breadcrumbItems={breadcrumbItems}>
              <CommonButton buttonsData={buttonsData} />
            </CommonBreadcrumb>
          </div>

          <div className={`bg-white overflow-hidden border rounded-[4px]`}>
            <div className="flex items-center bg-gray-200 p-2.5">
              <FaFileInvoiceDollar className="text-[18px] text-black " />
              <span className="text-[.935rem] text-black font-['Poppins'] ml-2 ">
                Purchase
              </span>
            </div>

            <div className="px-2 pt-1 pb-3">
              <Row gutter={[16, 8]}>
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <CustomInput
                    label="Name"
                    name="PurchaseName"
                    disabled={type === "info"}
                    required
                    loading={loading}
                  />
                </Col>
                {/* </Row>
                  <Row gutter={[16, 16]}> */}
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <CustomInput
                    label="Description"
                    name="PurchaseDesc"
                    disabled={type === "info"}
                    loading={loading}
                  />
                </Col>

                {!hideOemDealerForDealerAccount ? (
                  <>
                    {type === "info" ? (
                      <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                        <CustomInput
                          label="OEM"
                          name="OemName"
                          disabled
                          loading={loading}
                        />
                      </Col>
                    ) : (
                      <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                        <CustomSelect
                          label="OEM"
                          name="OemNo"
                          options={modifiedOems}
                          disabled={type === "info"}
                          placeholder="Please select"
                          onChange={(value) => {
                            setDefaultData((prev) => ({
                              ...prev,
                              OemNo: value,
                              DealerNo: null,
                            }));
                            setOemNo(value);
                            form.setFieldsValue({
                              DealerNo: null,
                            });
                          }}
                          required
                          handleCreateNew={handleCreateNew}
                          loading={optionDataLoading}
                          type={"OEM"}
                        />
                      </Col>
                    )}
                    <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                      <CustomSelect
                        label="Dealer"
                        name="DealerNo"
                        options={modifiedDealers}
                        disabled={
                          type === "info" || !form.getFieldValue("OemNo")
                        }
                        placeholder="Please select"
                        onChange={(value) => {
                          setDefaultData((prev) => ({
                            ...prev,
                            DealerNo: value,
                          }));
                        }}
                        required
                        handleCreateNew={handleCreateNew}
                        loading={dealerOptionsLoading}
                        type={"Dealer"}
                      />
                    </Col>
                  </>
                ) : null}
                {type === "info" && (
                  <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                    <CustomInput
                      label="Amount"
                      name="Amount"
                      disabled
                      loading={loading}
                    />
                  </Col>
                )}
                {type === "info" && (
                  <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                    <CustomInput
                      label="Discounted Amount"
                      name="Amount_with_discount"
                      disabled
                      loading={loading}
                    />
                  </Col>
                )}
                {type === "info" && (
                  <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                    <CustomDateTimePicker
                      name="PaymentTime"
                      label="Payment Time"
                      placeholder="Payment Time"
                      format="YYYY-MM-DD HH:mm" // 24-hour format
                      disabled
                      loading={loading}
                    />
                  </Col>
                )}
              </Row>
            </div>
          </div>
        </div>
      </Form>

      <ConfirmModal
        showModal={showModal}
        setShowModal={setShowModal}
        onDeleteConfirm={onDeleteConfirm}
      />
      <ConfirmModal
        showModal={showItemsModal}
        setShowModal={setShowItemsModal}
        onDeleteConfirm={onDeleteItemsConfirm}
        setSelectedRowKeys={setSelectedRowKeys}
      />

      <Modal
        title={
          <div className="flex items-center justify-between bg-gray-200 p-2.5 rounded-md">
            <div className="flex items-center">
              <PaymentIcon className="text-[18px] text-black " />
              <span className="text-[14px] font-[Poppins] text-black  ml-2">
                Make Payment
              </span>
            </div>
            {/* {![7, 8].includes(role_value) && (
              <Button
                type="primary"
                className="min-w-[140px] h-[30px] py-1 px-[10px] font-medium"
                onClick={() => setShowCardLayout((prev) => !prev)}
              >
                {showCardLayout ? "Manual Payment" : "Pay with Card"}
              </Button>
            )} */}
          </div>
        }
        open={paymentModal}
        onCancel={() => {
          setPaymentModal(false);
          setShowCardLayout(!isSystemUser);
        }}
        footer={null}
        closeIcon={false}
        width={560}
        centered
        destroyOnClose
      >
        <div>
          <MyPaymentForm
            DealerNo={
              data?.data?.DealerNo ??
              (hideOemDealerForDealerAccount ? getDealerNo() : undefined)
            }
            amount={purchaseAmountProp}
            setPaymentModal={setPaymentModal}
            InvoiceNo={id}
            OrderName={data?.data?.PurchaseName}
            refetch={refetch}
            showCardLayout={showCardLayout}
            setShowCardLayout={setShowCardLayout}
            onPaymentSuccess={async (paymentResponse) => {
              const result = await putAction(
                getPurchasePaidPayload(paymentResponse),
              );
              if (result != null) {
                refetch();
              }
            }}
          />
        </div>
      </Modal>
      {type === "info" && (
        <>
          <div className="flex justify-end mb-2">
            {!isPaid ? (
              <CommonButton buttonsData={purchaseItemButtonsData} />
            ) : (
              <div className="mt-2"></div>
            )}
          </div>
          <GlobalTable
            columns={PurchaseItemsColumns()}
            dataSource={
              purchaseData?.data
                ?.map((item) => ({
                  ...item,
                  key: item.ItemNo,
                }))
                ?.sort((a, b) => b.ItemNo - a.ItemNo) || []
            }
            rowSelection={!isPaid ? rowSelection : null}
            // onRow={onRow}
            // onRow={(record, rowIndex) => {
            //   return {
            //     onMouseEnter: () => setHoveredRow(rowIndex),
            //     onMouseLeave: () => setHoveredRow(null),
            //   };
            // }}
            loading={purchaseLoading}
          />
          <PurchaseItemModal
            isOpenModal={openModal}
            setIsOpenModal={setOpenModal}
            productionRefetch={purchaseRefetch}
            purchaseRefetch={refetch}
            title={
              <div className="flex items-center bg-gray-200 p-2 rounded-md">
                <FaFileInvoiceDollar className="text-[18px] text-black " />
                <span className="text-[14px] font-[Poppins] text-black  ml-2">
                  Purchase Item
                </span>
              </div>
            }
          />
        </>
      )}
    </>
  );
};
